/**
 * Auth API Service
 * 
 * Endpoints:
 * POST /auth/register - Benutzer registrieren
 * POST /auth/login    - Login (setzt JSESSIONID Cookie)
 * POST /auth/logout   - Logout (invalidiert Session)
 * GET  /auth/me       - Session Check
 */

import { post, get } from '../client';

/**
 * Benutzer registrieren
 * @param {object} credentials - { email, username, password }
 * @returns {Promise<{success: boolean, data?: {message: string}, error?: object}>}
 * 
 * Errors: 409 USER_EXISTS
 */
export const register = async ({ email, username, password }) => {
  return post('/auth/register', { email, username, password });
};

/**
 * Login
 * @param {object} credentials - { username, password }
 * @returns {Promise<{success: boolean, data?: {user_id: number}, error?: object}>}
 * 
 * Errors: 401 UNAUTHORIZED (INVALID_CREDENTIALS)
 */
export const login = async ({ username, password }) => {
  return post('/auth/login', { username, password });
};

/**
 * Logout
 * @returns {Promise<{success: boolean, data?: {message: string}, error?: object}>}
 * 
 * Errors: 401 UNAUTHORIZED
 */
export const logout = async () => {
  return post('/auth/logout');
};

/**
 * Session Check - Prüft ob User eingeloggt ist
 * @returns {Promise<{success: boolean, data?: {id: number, username: string, email: string, role: number}, error?: object}>}
 * 
 * Errors: 401 UNAUTHORIZED
 */
export const getMe = async () => {
  return get('/auth/me');
};

/**
 * Helper: Prüft ob Session gültig ist
 * @returns {Promise<boolean>}
 */
export const isAuthenticated = async () => {
  const result = await getMe();
  return result.success;
};

/**
 * Verifizierungs-Mail erneut anfordern
 * @param {string} email
 * @returns {Promise<{success: boolean, data?: {message: string}, error?: object}>}
 *
 * Errors: 429 RESEND_COOLDOWN
 */
export const resendVerification = async (email) => {
  return post('/auth/resend-verification', { email });
};

/**
 * Bestätigt die E-Mail-Adresse anhand des Tokens aus der Verifizierungs-Mail
 * @param {string} token
 * @returns {Promise<{success: boolean, data?: {message: string}, error?: object}>}
 *
 * Errors: 400/404 INVALID_TOKEN
 */
export const verifyEmail = async (token) => {
  return post('/auth/verify-email', { token });
};

/**
 * Passwort-vergessen-Flow auslösen (Antwort ist immer generisch erfolgreich)
 * @param {string} email
 * @returns {Promise<{success: boolean, data?: {message: string}, error?: object}>}
 */
export const forgotPassword = async (email) => {
  return post('/auth/forgot-password', { email });
};

/**
 * Passwort anhand des Tokens aus der Reset-Mail neu setzen
 * @param {string} token
 * @param {string} newPassword
 * @returns {Promise<{success: boolean, data?: {message: string}, error?: object}>}
 *
 * Errors: 400 VALIDATION_ERROR / INVALID_TOKEN
 */
export const resetPassword = async (token, newPassword) => {
  return post('/auth/reset-password', { token, new_password: newPassword });
};
